import React, { useState } from 'react';
import CheckCircleFilledIcon from 'design-system/src/assets/icons/checkCircleFilled.svg?react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { Input } from 'design-system/src/components/Input/Input';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { createTrackingProps } from 'shared-utils/analytics';
import { getEhrIntegrationConfig } from '../../tasks/EhrIntegrationConfig';
import type { EhrIntegrationFormProps, EhrCredentials } from './EhrIntegrationForm';
import { CopyableDetailsTable } from '../../CopyableDetailsTable';
import { generateEhrEmail, generateEhrLastName } from './utils';
import { EhrIntegrationSuccessCard } from './EhrIntegrationSuccessCard';
import { FormContainer, BodyContainer, FormCard, FormFieldsContainer } from './styles';
import { InstructionsTitle } from './InstructionsTitle';
import { InstructionsFooter } from './InstructionsFooter';

type FormField = {
  key: string;
  label: string;
  placeholder: string;
};

/**
 * EHR integration instructions component for drchrono
 */
export const DrchronoInstructions = ({
  className,
  ehrType,
  practiceName,
  isCompleted = false,
  onSubmit,
  isSubmitLoading = false,
  isSubmitted = false,
  source,
}: EhrIntegrationFormProps) => {
  const config = getEhrIntegrationConfig(ehrType, practiceName);
  const ehrDisplayName = config.ehrName;
  const [credentials, setCredentials] = useState<EhrCredentials>({});

  const formFields: FormField[] = [
    {
      key: 'url',
      label: `${ehrDisplayName} URL`,
      placeholder: 'your-practice-url.com',
    },
    {
      key: 'username',
      label: 'Username',
      placeholder: 'Username (created by you)',
    },
    {
      key: 'password',
      label: 'Password',
      placeholder: 'Enter password',
    },
    {
      key: 'client_id',
      label: 'Client ID',
      placeholder: 'Enter Client ID',
    },
    {
      key: 'client_secret',
      label: 'Client Secret',
      placeholder: 'Enter Client Secret',
    },
  ];

  const isFormValid = formFields.every((field) => Boolean(credentials[field.key]));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // make sure users can't submit using enter key on an input field if form is invalid (submit button is also disabled)
    if (isCompleted || !isFormValid) return;
    onSubmit?.(credentials);
  };

  const submitButton = (
    <Button
      size="sm"
      type="submit"
      variant="primary"
      disabled={!isFormValid || isCompleted}
      isLoading={isSubmitLoading}
      width="full"
      {...createTrackingProps({
        elementName:
          source === 'onboardingFlow'
            ? 'Onboarding Flow: EHR Integration Submit Button'
            : 'Settings Page: EHR Integration Tab Submit Button',
        unmask: true,
      })}
    >
      Submit
    </Button>
  );
  // TODO(design-system): move this disabled tooltip logic into the Button component
  const buttonWithPossibleTooltip =
    !isFormValid || isCompleted ? (
      <Tooltip
        content={isCompleted ? 'EHR integration is already completed.' : 'Please complete all fields to continue.'}
        childNotInteractive
        // allows button to be full width
        focusableWrapperClassName="w-full"
      >
        {submitButton}
      </Tooltip>
    ) : (
      submitButton
    );

  return (
    <FormContainer className={className}>
      <InstructionsTitle ehrDisplayName={ehrDisplayName} titleText={`Connect with ${ehrDisplayName} through API`} />
      <Hairline className="!my-8" />
      <BodyContainer>
        <ol className="m-0 flex w-full list-none flex-col gap-8 p-0">
          <li className="flex flex-col gap-3">
            <Typography weight={600}>
              1. Create a <span className="italic">new</span> account in {ehrDisplayName}
            </Typography>

            <Typography>
              Create a new user in {ehrDisplayName} with the <span className="font-bold">Medical Assistant</span> role
              using the credentials below.
            </Typography>

            <InfoBox variant="surface" noTopMargin noBottomMargin>
              <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
                Only Admin users in {ehrDisplayName} can create new accounts
              </Typography>
              <Typography styledAs="bodySmallDMSans">
                If you do not see the Admin tab in your {ehrDisplayName} menu, please ask an administrator at your
                practice to complete this step.
              </Typography>
            </InfoBox>

            <CopyableDetailsTable
              rows={[
                {
                  label: 'Email',
                  value: generateEhrEmail(practiceName),
                  showCopyButton: true,
                },
                {
                  label: 'First Name',
                  value: 'Access Support',
                  showCopyButton: true,
                },
                {
                  label: 'Last Name',
                  value: generateEhrLastName(practiceName),
                  showCopyButton: true,
                },
                {
                  label: 'DOB',
                  value: '04/15/1995',
                  showCopyButton: true,
                },
                {
                  label: 'Phone Number',
                  value: "Use your practice's phone number",
                  showCopyButton: false,
                },
                {
                  label: 'Role',
                  value: 'Medical Assistant',
                  showCopyButton: false,
                },
              ]}
            />

            <div className="mx-auto w-full max-w-[600px]">
              <Typography weight={600}>Notes:</Typography>
              <ul className="list-none pl-8">
                <li className="mt-3 flex gap-3">
                  <CheckCircleFilledIcon aria-hidden />
                  <Typography styledAs="bodyExtraSmallSpaceGrotesk">
                    Ensure this user has access to patient charts and lab results
                  </Typography>
                </li>
                <li className="mt-3 flex gap-3">
                  <CheckCircleFilledIcon aria-hidden />
                  <Typography styledAs="bodyExtraSmallSpaceGrotesk">
                    Provision after-hours (nights and weekends) access
                  </Typography>
                </li>
              </ul>
            </div>
          </li>

          <li className="flex flex-col gap-3">
            <Typography weight={600}>2. Create a new API application</Typography>

            <Typography>
              In {ehrDisplayName}, go to <span className="font-bold">Account &gt; Provider Settings &gt; API</span> and
              create a new API application using the details below.
            </Typography>

            <CopyableDetailsTable
              rows={[
                {
                  label: 'Name',
                  value: 'TandemAccessApp',
                  showCopyButton: true,
                },
                {
                  label: 'Redirect URLs',
                  value: 'https://localhost',
                  showCopyButton: true,
                },
                {
                  label: 'API version',
                  value: 'v4 (Hunt Valley)',
                  showCopyButton: false,
                },
              ]}
            />
          </li>

          <li className="flex flex-col gap-3">
            <Typography weight={600}>3. Send account credentials and API credentials to Tandem</Typography>

            <Typography>
              Once you've completed the setup above, fill out the form below so we can connect your EHR.
            </Typography>

            {(isSubmitted || isCompleted) && !isSubmitLoading ? (
              <EhrIntegrationSuccessCard ehrDisplayName={ehrDisplayName} />
            ) : (
              <FormCard>
                <form onSubmit={handleSubmit}>
                  <FormFieldsContainer>
                    {formFields.map((field) => (
                      <Input
                        key={field.key}
                        label={field.label}
                        inputSize="sm"
                        type="text"
                        value={credentials[field.key] || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setCredentials((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        required
                        placeholder={field.placeholder}
                        disabled={isSubmitLoading}
                      />
                    ))}

                    <ButtonRow align={Alignment.Left}>{buttonWithPossibleTooltip}</ButtonRow>
                  </FormFieldsContainer>
                </form>
              </FormCard>
            )}
          </li>
        </ol>
      </BodyContainer>
      <InstructionsFooter />
    </FormContainer>
  );
};
